import { Line, MultilineInput, Span, TableBuilder, TextInput } from "@tblabs/truffle";
import { ToDo } from "../../Components/ToDo";
import { DateTimeEditInput } from "../../Components/Utils/DateSlider";
import { TimeUtils } from "../../Utils/TimeUtils";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent, StepEventType } from "../Step/StepEvent";
import { StepType } from "../Step/StepType";
import { PersonalPickupStepState } from "./Models/PersonalPickupStepState";
import { MeetingInfoForCustomer } from "./Components/MeetingInfoForCustomer";
import { MeetingInfoForAdmin } from "./Components/MeetingInfoForAdmin";
import { PersonalPickupStepData } from "./Models/PersonalPickupStepData";
import { Agent, AgentsProvider } from "../../Services/Agents/AgentsProvider";
import { Box } from "../../Components/Utils/Box";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { CashType } from "../Step/CashType";


export class PersonalPickupStep extends Step<PersonalPickupStepState>
{
    public Type = StepType.PersonalPickup;
    public Icon = "🐤";
    public TitleForCustomer = () => `Odbiór osobisty`;
    public Title = () => `Odbiór osobisty (${this.Data.MeetingPlace.value || "miejsce nieustalone"})`;

    public InitialState = PersonalPickupStepState.NotPlannedYet;
    public WellDoneStates = [PersonalPickupStepState.MeetingOk];
    public FailedDoneStates = [PersonalPickupStepState.MeetingFailed, PersonalPickupStepState.Canceled, PersonalPickupStepState.CanceledDespitePlanned];

    public get AsEvents(): StepEvent[]
    {
        return [
            new StepEvent(StepEventType.PersonalPickup, this.Order.Id, "🐤", "Spotkanie", this.Data.PlannedDispatchTime.value, this.IsDone)
        ];
    }
    public get Mana(): Cash[]
    {
        const cash: Cash[] = [];

        if (this.Data.State.Is(PersonalPickupStepState.MeetingOk))
        {
            // LEGACY
            if (this.Data.PaymentAtDelivery.value > 0)
            {
                const extra = this.Data.PaymentAtDelivery.value - this.Order.DesiredDeposit;

                if (extra > 0)
                {
                    cash.push(new Cash(CashType.ReceivedIncome, this, extra, "Odbiór kasy podczas spotkania", new Date(+this.Data.DispatchTime.value || +this.Data.PlannedDispatchTime.value)))
                }

                if (this.Order.DesiredDeposit > 0)
                {
                    cash.push(new Cash(CashType.ReceivedDeposit, this, this.Order.DesiredDeposit, "Odbiór kaucji podczas spotkania", new Date(+this.Data.DispatchTime.value || +this.Data.PlannedDispatchTime.value)))
                }
            }
            else // CURRENT APPROACH
            {
                cash.push(new Cash(CashType.ReceivedIncome, this, this.Data.BasketSum.value, "Odbiór kasy podczas spotkania", new Date(+this.Data.DispatchTime.value || +this.Data.PlannedDispatchTime.value)))
                cash.push(new Cash(CashType.ReceivedDeposit, this, this.Data.DepositSum.value, "Odbiór kaucji podczas spotkania", new Date(+this.Data.DispatchTime.value || +this.Data.PlannedDispatchTime.value)))
            }
        }

        return cash;
    }
    public get WhenPlanned(): Date
    {
        return this.Data.PlannedDispatchTime.value;
    }
    public get WhenDone(): Date
    {
        return this.Data.DispatchTime.value;
    }

    constructor(private _agent: AgentsProvider, public Data: PersonalPickupStepData)
    {
        super(Data);
    }

    public Init(): void
    { }

    public STATES = {
        [PersonalPickupStepState.NotPlannedYet]: {
            StateName: "Jeszcze nie zaplanowane",
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [`Spotkanie nie zostało jeszcze zaplanowane. Jeśli zbliża się termin, możesz do nas zadzwonić i ustalić dogodny czas`],
            },
            Admin: {
                Content: () => [
                    `Czas spotkania nie został jeszcze zaplanowany`,
                    new MeetingInfoForAdmin(this._agent, this.Order, this.Data),
                    new ToDo(
                        `Przypisz Agenta oraz ustal miejsce i czas spotkania\n`,
                        new DateTimeEditInput(this.Data.PlannedDispatchTime))
                ],
                Actions: {
                    "✅": () =>
                    {
                        this.Data.AssignedAgentId.value ||= Agent.Default.Id;
                        this.GoTo(PersonalPickupStepState.PrePlanned);
                    },
                    "❌": () => this.GoTo(PersonalPickupStepState.Canceled),
                },
            },
        },
        [PersonalPickupStepState.PrePlanned]: {
            StateName: `Spotkanie ${TimeUtils.TimeLeftTo(this.Data.PlannedDispatchTime, "(nie określono)", false)}`,
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [
                    `☑️ Wciąż ustalamy szczegóły spotkania`,
                    new MeetingInfoForCustomer(this._agent, this.Data),
                ],
            },
            Admin: {
                Content: () => [
                    `☑️ Szczegóły spotkania jeszcze nie ustalone`,
                    new MeetingInfoForAdmin(this._agent, this.Order, this.Data),
                    new ToDo(
                        `Ustal godzinę spotkania`,
                        new DateTimeEditInput(this.Data.PlannedDispatchTime)
                    )
                ],
                Actions: {
                    "✅": () => this.GoTo(PersonalPickupStepState.Planned),
                    "❌": () => this.GoTo(PersonalPickupStepState.Canceled),
                }
            }
        },
        [PersonalPickupStepState.Planned]: {
            StateName: "Spotkanie " + TimeUtils.TimeLeftTo(this.Data.PlannedDispatchTime, "(nie określono)", false),
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [
                    `Szczegóły spotkania:`,
                    new MeetingInfoForCustomer(this._agent, this.Data),
                    // `Jeżeli chcesz zmienić porę spotkania, zadzwoń do swojego opiekuna: ☎️ ${this._agent.Get(this.Data.AssignedAgentId.value)?.Phone || "507-293-714"}`
                    ],
            },
            Admin: {
                Content: () => [
                    `✅ Spotkanie zaplanowane`,
                    new MeetingInfoForAdmin(this._agent, this.Order, this.Data),
                    new ToDo(`Czy spotkanie się odbyło?`)
                ],
                Actions: {
                    "✅": () => this.GoTo(PersonalPickupStepState.AfterMeeting),
                    "❌": () => this.GoTo(PersonalPickupStepState.CanceledDespitePlanned),
                }
            }
        },
        [PersonalPickupStepState.AfterMeeting]: {
            StateName: "Po spotkaniu",
            Customer: {
                Content: () => [`✅ Już po spotkaniu`],
            },
            Admin: {
                Content: () => [
                    `✅ Spotkanie już się odbyło`,
                    new ToDo(`Wpisz numery seryjne\n`,
                        new TableBuilder(this.Order.BasketItems).Class("small-table")
                            .AddColumn("Produkt", x => x.ProductInfo.Name + " ×" + x.Quantity.value)
                            .AddColumn("Numer seryjny", x => new TextInput(x.ProductSerialNumber))
                            .AddColumn("Dane dostępowe", x => new TextInput(x.AccessData))
                            .Build(),
                        `\nKomentarz na temat spotkania:\n`,
                        new MultilineInput(this.Data.MeetupNotes).Rows(4).Placeholder("Opis klienta i/lub spotkania"),
                    ),
                ],
                Actions: {
                    "✅": () => { this.Data.DispatchTime.value = DateTimeProvider.Now; this.GoTo(PersonalPickupStepState.MeetingOk); },
                    "❌": () => this.GoTo(PersonalPickupStepState.MeetingFailed),
                }
            }
        },
        [PersonalPickupStepState.MeetingOk]: {
            StateName: "Spotkanie poszło dobrze",
            Customer: {
                Content: () => [
                    `✅ Już po spotkaniu`,
                ],
            },
            Admin: {
                Content: () => [`✅ Spotkanie poszło dobrze`,
                    this.Data.MeetupNotes.value && new Box(
                        new Line().MarginBottom(12),
                        "Notatka ze spotkania:\n",
                        new Span(this.Data.MeetupNotes.value).Italic()).DisplayBlock().MarginTop(16).TextAlignLeft(),
                    '\n\nPobrana gotówka: ' + this.Data.BasketSum.value + ' zł + depozyt: ' + this.Data.DepositSum.value + ' zł',
                ],
            }
        },
        [PersonalPickupStepState.MeetingFailed]: {
            StateName: "Spotkanie nieudane",
            Customer: {
                Content: () => [`❌ Coś poszło nie tak`],
            },
            Admin: {
                Content: () => [`Spotkanie nieudane: ${this.Data.MeetupNotes.value}`],
            }
        },
        [PersonalPickupStepState.Canceled]: {
            StateName: "Odwołane",
            Customer: {
                Content: () => [`❌ Spotkanie zostało odwołane`],
            },
            Admin: {
                Content: () => [`Spotkanie zostało odwołane zanim się odbyło`],
            }
        },
        [PersonalPickupStepState.CanceledDespitePlanned]: {
            StateName: "Odwołane pomimo zaplanowania",
            Customer: {
                Content: () => [`❌ Spotkanie zostało odwołane`],
            },
            Admin: {
                Content: () => [`Spotkanie zostało odwołane pomimo zaplanowania na ${TimeUtils.FormattedDateTime(this.Data.PlannedDispatchTime)}`],
            }
        },
    };
}